import type { WelcomeVideoConfig } from '@/core/domain/entities/WelcomeVideo';

export type WelcomeVideoSource =
  | { provider: 'youtube'; videoId: string; posterSrc: string; captionsSrc?: string }
  | { provider: 'file'; videoSrc: string; hlsSrc?: string; posterSrc?: string; captionsSrc?: string };

export type WelcomeFileSource = Extract<WelcomeVideoSource, { provider: 'file' }>;

function mediaUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim()) return undefined;
  const trimmed = value.trim();
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) return trimmed;
  try {
    const url = new URL(trimmed);
    return url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}

export function getWelcomeVideoSource(config: WelcomeVideoConfig | null): WelcomeVideoSource | null {
  if (!config || config.enabled !== true) return null;
  const source = mediaUrl(config.video_url);
  if (!source) return null;
  const url = new URL(source, 'https://shop.motito.co');
  const host = url.hostname.toLowerCase();
  const youtube = ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(host);
  if (youtube || host === 'youtu.be' || host === 'www.youtu.be') {
    const pieces = url.pathname.split('/').filter(Boolean);
    const id = host.endsWith('youtu.be') ? pieces[0] : pieces.length === 1 && pieces[0] === 'watch' ? url.searchParams.get('v') : ['embed', 'shorts', 'live'].includes(pieces[0] ?? '') ? pieces[1] : undefined;
    if (!id || !/^[a-zA-Z0-9_-]{11}$/.test(id)) return null;
    return { provider: 'youtube', videoId: id, posterSrc: mediaUrl(config.poster_url) ?? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` };
  }
  if (!/\.(mp4|webm|ogv|ogg)$/i.test(url.pathname)) return null;
  const candidate = mediaUrl(config.hls_url);
  const hlsSrc = candidate && /\.m3u8$/i.test(new URL(candidate, 'https://shop.motito.co').pathname) ? candidate : undefined;
  return { provider: 'file', videoSrc: source, ...(hlsSrc ? { hlsSrc } : {}), posterSrc: mediaUrl(config.poster_url), captionsSrc: mediaUrl(config.captions_url) };
}
